import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useMatch, useNavigate } from 'react-router-dom';
import { auth } from '../lib/api.js';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Logo } from '../components/Logo.tsx';
import { Button, Loading, Notice } from '../components/ui.tsx';

export interface OwnerContext {
  ownerId: string;
  email: string;
  limits: { maxActiveConversations: number; maxInvitesPerDay: number; maxUploadBytes: number };
}

/**
 * Owner application shell.
 *
 * Desktop gets a persistent sidebar: navigation stays visible, the current
 * section is obvious, and the content column keeps a comfortable measure.
 *
 * Below 900px the sidebar becomes a slim header plus a bottom tab bar, which is
 * where a thumb actually reaches. Inside a conversation both are hidden — a
 * chat wants the whole screen, and a tab bar would compete with the composer
 * for the bottom edge.
 */

const NAV: Array<{ to: string; label: string; icon: IconName }> = [
  { to: '/app/conversations', label: 'Conversations', icon: 'message' },
  { to: '/app/profile', label: 'Profile', icon: 'user' },
  { to: '/app/branding', label: 'Branding', icon: 'palette' },
  { to: '/app/settings', label: 'Settings', icon: 'settings' },
];

function initials(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : local.slice(0, 2);
  return letters.toUpperCase() || '?';
}

export function OwnerLayout() {
  const navigate = useNavigate();
  const [owner, setOwner] = useState<OwnerContext | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'signed-out' | 'suspended'>('loading');
  const [suspensionReason, setSuspensionReason] = useState<string | null>(null);

  // A conversation takes the whole screen on small viewports.
  const inConversation = useMatch('/app/conversations/:id') !== null;

  useEffect(() => {
    auth
      .session()
      .then((session) => {
        if (session.owner.suspendedAt) {
          setSuspensionReason(session.owner.suspensionReason);
          setState('suspended');
          return;
        }
        setOwner({ ownerId: session.owner.id, email: session.owner.email, limits: session.limits });
        setState('ready');
      })
      .catch(() => setState('signed-out'));
  }, []);

  if (state === 'loading') {
    return (
      <div className="shell">
        <Loading>Loading your account…</Loading>
      </div>
    );
  }

  if (state === 'signed-out') {
    return (
      <div className="shell">
        <div className="page page--narrow">
          <div className="empty">
            <span className="empty__icon">
              <Icon name="lock" size={22} />
            </span>
            <h3>Please sign in</h3>
            <p>Your session has ended, or this browser is not signed in.</p>
            <Link className="btn btn--primary" to="/login" style={{ marginTop: 'var(--s-2)' }}>
              <Icon name="mail" />
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'suspended') {
    return (
      <div className="shell">
        <div className="page page--narrow">
          <h1 style={{ marginBottom: 'var(--s-4)' }}>Account suspended</h1>
          <Notice tone="danger" title="This account cannot be used">
            <p>{suspensionReason ?? 'This account has been suspended.'}</p>
            <p>Existing links have been revoked and new ones cannot be created.</p>
          </Notice>
        </div>
      </div>
    );
  }

  const signOut = () => void auth.signOut().finally(() => navigate('/login'));

  return (
    <div className={inConversation ? 'app app--immersive' : 'app'}>
      <aside className="sidebar">
        <Link className="sidebar__brand" to="/app/conversations" aria-label="Kairelo">
          <Logo size={26} />
        </Link>

        <nav aria-label="Sections">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? 'navlink is-active' : 'navlink')}
            >
              <Icon name={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="sidebar__account">
            <span className="avatar" aria-hidden="true">
              {owner ? initials(owner.email) : '?'}
            </span>
            <span className="sidebar__email" title={owner?.email}>
              {owner?.email}
            </span>
          </div>
          <Button variant="ghost" icon="logout" onClick={signOut} block>
            Sign out
          </Button>
        </div>
      </aside>

      {/* Compact header for small screens. */}
      <header className="mobilebar">
        <Link className="mobilebar__brand" to="/app/conversations">
          <span className="sidebar__mark" aria-hidden="true">
            <Icon name="message" size={14} />
          </span>
          Kairelo
        </Link>
        <span className="spacer" />
        <Button variant="ghost" size="sm" iconOnly icon="logout" onClick={signOut}>
          Sign out
        </Button>
      </header>

      <main className="app__main">
        <Outlet context={owner satisfies OwnerContext | null} />
      </main>

      <nav className="tabbar" aria-label="Sections">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => (isActive ? 'is-active' : undefined)}
          >
            <Icon name={item.icon} size={20} />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
